import { accessGroupSchema } from '@/access-group'
import { scalarAdminSchema } from '@/admins'
import {
  identityProviderRecordSchema,
  oauthStateSchema,
  personalTokenRecordSchema,
  samlStateSchema,
} from '@/auth'
import { siteDeploySchema } from '@/deploys'
import { loginPortalSchema } from '@/login-portals'
import { managedDocSchema } from '@/managed-doc'
import { managedSchema } from '@/managed-schema'
import {
  githubProjectSchema,
  projectSchema,
  publishRecordSchema,
  signupDataSchema,
} from '@/project'
import { ruleSchema } from '@/rule'
import { sdkBuildSchema, sdkSchema } from '@/sdk'
import { teamSchema } from '@/team'
import { themeSchema } from '@/theme'
import {
  exchangeTokenSchema,
  hostingExchangeTokenSchema,
  refreshTokenRecordSchema,
} from '@/token'
import {
  emailSignupOtpSchema,
  userGithubSchema,
  userPasswordSchema,
  userSchema,
} from '@/user'
import { bannedUserSchema } from '@/user/ban'
import { signupMetaSchema } from '@/user/signup-meta'
import { waitlistRecordSchema } from '@/waitlist'
import { workspaceSchema } from '@/workspace'

/** List of collection name generators for Scalar collections */
export const BaseCollections = {
  Users: 'users',
  ScalarAdmins: 'admin-users',
  SignupMeta: 'signup-meta',
  ExchangeTokens: 'exchange-tokens',
  HostingExchange: 'hosting-exchange',
  Passwords: 'passwords',
  SignupOtp: 'signup-otp',
  GithubUsers: 'github-users',
  RefreshTokens: 'refresh-tokens',
  Teams: 'team',
  Deploys: 'deploys',
  SamlStates: 'saml-states',
  SignupData: 'signup-data',
  Projects: (teamUid: string) => `team/${teamUid}/projects`,
  GithubProjects: (teamUid: string) => `team/${teamUid}/projects-github`,
  Themes: (teamUid: string) => `team/${teamUid}/themes`,
  PublishRecords: (teamUid: string) => `team/${teamUid}/publish`,
  ManagedDocs: (teamUid: string) => `team/${teamUid}/managed-docs`,
  ManagedSchemas: (teamUid: string) => `team/${teamUid}/managed-schemas`,
  Rules: (teamUid: string) => `team/${teamUid}/rules`,
  AccessGroups: (teamUid: string) => `team/${teamUid}/access-groups`,
  LoginPortals: (teamUid: string) => `team/${teamUid}/login-portals`,
  Sdks: (teamUid: string) => `team/${teamUid}/sdks`,
  SdkBuilds: (teamUid: string) => `team/${teamUid}/sdk-builds`,
  PersonalTokens: (userUid: string) => `user/${userUid}/personal-tokens`,
  IdentityProviders: (teamUid: string) => `team/${teamUid}/identity-providers`,
  Workspaces: (teamUid: string) => `team/${teamUid}/workspaces`,
  OauthStates: 'oauth-states',
  Waitlist: 'waitlist',
  BannedUsers: 'banned-users',
} as const

export const MONGO_PLACEHOLDER_UID = '<uid-0>'

/** Complete collection definitions for the mongo database */
export const collectionDefinitions = {
  users: {
    schema: userSchema,
    path: () => BaseCollections.Users,
  },
  scalarAdmins: {
    schema: scalarAdminSchema,
    path: () => BaseCollections.ScalarAdmins,
  },
  signupData: {
    schema: signupDataSchema,
    path: () => BaseCollections.SignupData,
  },
  signupOtp: {
    schema: emailSignupOtpSchema,
    path: () => BaseCollections.SignupOtp,
  },
  refreshTokens: {
    schema: refreshTokenRecordSchema,
    path: () => BaseCollections.RefreshTokens,
  },
  exchangeTokens: {
    schema: exchangeTokenSchema,
    path: () => BaseCollections.ExchangeTokens,
  },
  hostingExchange: {
    schema: hostingExchangeTokenSchema,
    path: () => BaseCollections.HostingExchange,
  },
  passwords: {
    schema: userPasswordSchema,
    path: () => BaseCollections.Passwords,
  },
  githubUsers: {
    schema: userGithubSchema,
    path: () => BaseCollections.GithubUsers,
  },
  personalTokens: {
    schema: personalTokenRecordSchema,
    path: ({ userUid }: { userUid: string }) =>
      BaseCollections.PersonalTokens(userUid),
  },
  team: {
    schema: teamSchema,
    path: () => BaseCollections.Teams,
  },
  projects: {
    schema: projectSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      BaseCollections.Projects(teamUid),
  },
  githubProjects: {
    schema: githubProjectSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      BaseCollections.GithubProjects(teamUid),
  },
  publishRecords: {
    schema: publishRecordSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      BaseCollections.PublishRecords(teamUid),
  },
  managedDocs: {
    schema: managedDocSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      BaseCollections.ManagedDocs(teamUid),
  },
  managedSchemas: {
    schema: managedSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      BaseCollections.ManagedSchemas(teamUid),
  },
  themes: {
    schema: themeSchema,
    path: ({ teamUid }: { teamUid: string }) => BaseCollections.Themes(teamUid),
  },
  rules: {
    schema: ruleSchema,
    path: ({ teamUid }: { teamUid: string }) => BaseCollections.Rules(teamUid),
  },
  accessGroups: {
    schema: accessGroupSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      BaseCollections.AccessGroups(teamUid),
  },
  loginPortals: {
    schema: loginPortalSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      BaseCollections.LoginPortals(teamUid),
  },
  sdks: {
    schema: sdkSchema,
    path: ({ teamUid }: { teamUid: string }) => BaseCollections.Sdks(teamUid),
  },
  sdkBuilds: {
    schema: sdkBuildSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      BaseCollections.SdkBuilds(teamUid),
  },
  identityProviders: {
    schema: identityProviderRecordSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      BaseCollections.IdentityProviders(teamUid),
  },
  workspaces: {
    schema: workspaceSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      BaseCollections.Workspaces(teamUid),
  },
  deploys: {
    schema: siteDeploySchema,
    path: () => BaseCollections.Deploys,
  },
  samlStates: {
    schema: samlStateSchema,
    path: () => BaseCollections.SamlStates,
  },
  oauthStates: {
    schema: oauthStateSchema,
    path: () => BaseCollections.OauthStates,
  },
  waitlist: {
    schema: waitlistRecordSchema,
    path: () => BaseCollections.Waitlist,
  },
  bannedUsers: {
    schema: bannedUserSchema,
    path: () => BaseCollections.BannedUsers,
  },
  signupMeta: {
    schema: signupMetaSchema,
    path: () => BaseCollections.SignupMeta,
  },
} as const

export type CollectionDefinition =
  (typeof collectionDefinitions)[CollectionKeys]
export type CollectionKeys = keyof typeof collectionDefinitions

/**
 * Get the collection definitions for a list of collection keys
 *
 * Typescript gymnastics required to ensure we get a strict subset as a constant object
 */
export function getCollectionDefinitions<T extends CollectionKeys[]>(
  collections: T,
): { [x in T[number]]: (typeof collectionDefinitions)[x] } {
  const collectionDefs = {} as {
    [x in T[number]]: (typeof collectionDefinitions)[x]
  }

  collections.forEach((collection: T[number]) => {
    collectionDefs[collection] = collectionDefinitions[collection]
  })
  return collectionDefs
}

/**
 * Legacy format to test backwards compatibility
 * @deprecated Used to test backwards compatibility
 */
export const _legacyDefs = {
  users: {
    schema: userSchema,
    path: () => 'users',
  },
  signupData: {
    schema: signupDataSchema,
    path: () => 'signup-data',
  },
  exchangeTokens: {
    schema: exchangeTokenSchema,
    path: () => 'exchange-tokens',
  },
  passwords: {
    schema: userPasswordSchema,
    path: () => 'passwords',
  },
  githubUsers: {
    schema: userGithubSchema,
    path: () => 'github-users',
  },
  refreshTokens: {
    schema: refreshTokenRecordSchema,
    path: () => 'refresh-tokens',
  },
  personalTokens: {
    schema: personalTokenRecordSchema,
    path: ({ userUid }: { userUid: string }) =>
      `user/${userUid}/personal-tokens`,
  },
  team: {
    schema: teamSchema,
    path: () => 'team',
  },
  projects: {
    schema: projectSchema,
    path: ({ teamUid }: { teamUid: string }) => `team/${teamUid}/projects`,
  },
  githubProjects: {
    schema: githubProjectSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      `team/${teamUid}/projects-github`,
  },
  publishRecords: {
    schema: publishRecordSchema,
    path: ({ teamUid }: { teamUid: string }) => `team/${teamUid}/publish`,
  },
  sdkBuilds: {
    schema: sdkBuildSchema,
    path: ({ teamUid }: { teamUid: string }) => `team/${teamUid}/sdk-builds`,
  },
  managedDocs: {
    schema: managedDocSchema,
    path: ({ teamUid }: { teamUid: string }) => `team/${teamUid}/managed-docs`,
  },
  managedSchemas: {
    schema: managedSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      `team/${teamUid}/managed-schemas`,
  },
  themes: {
    schema: themeSchema,
    path: ({ teamUid }: { teamUid: string }) => `team/${teamUid}/themes`,
  },
  rules: {
    schema: ruleSchema,
    path: ({ teamUid }: { teamUid: string }) => `team/${teamUid}/rules`,
  },
  accessGroups: {
    schema: accessGroupSchema,
    path: ({ teamUid }: { teamUid: string }) => `team/${teamUid}/access-groups`,
  },
  loginPortals: {
    schema: loginPortalSchema,
    path: ({ teamUid }: { teamUid: string }) => `team/${teamUid}/login-portals`,
  },
  sdks: {
    schema: sdkSchema,
    path: ({ teamUid }: { teamUid: string }) => `team/${teamUid}/sdks`,
  },
  identityProviders: {
    schema: identityProviderRecordSchema,
    path: ({ teamUid }: { teamUid: string }) =>
      `team/${teamUid}/identity-providers`,
  },
  workspaces: {
    schema: workspaceSchema,
    path: ({ teamUid }: { teamUid: string }) => `team/${teamUid}/workspaces`,
  },
  samlStates: {
    schema: samlStateSchema,
    path: () => 'saml-states',
  },
  deploys: {
    schema: siteDeploySchema,
    path: () => 'deploys',
  },
  oauthStates: {
    schema: oauthStateSchema,
    path: () => 'oauth-states',
  },
} as const
